import { MODELS, MAX_GENERATION_SECONDS, reserveFor, type ShotSpec, type JobKind } from './index';
import { routeFor, routeReservation, type ProviderProfile } from './provider-profiles';
import type { ProjectDetail } from './view';
import { takeAllowance, takeSegments } from './take-plan';

export const sourceDuration = (shot: ShotSpec) => shot.generationDuration ?? shot.duration;

/** Reviewed command limit, shared by Lab admission and controls; the full estimate stays visible. */
export function episodeCommandAllowance(estimate: number, profile?: ProviderProfile) {
  return Math.min(estimate, profile?.maxCostMicros ?? Infinity);
}
/**
 * Whether a shot is filmed twice so the better take can be kept.
 *
 * The first three seconds decide whether a viewer stays and the last shot whether they come back,
 * and a generated take varies more from one attempt to the next than any setting changes it. So
 * the opening and closing shots get a second take from the same approved reference, and the video
 * check keeps whichever is better. Only native-length Omni shots: a Veo take is reserved at six
 * dollars, and an extended take is several requests already. The Tier 1 account allows twenty
 * Omni requests a day, so this is two more per episode, not one more per shot.
 */
export function takesTwice(shot: ShotSpec, orders: number[], model: string = MODELS.video) {
  return (
    model === MODELS.video &&
    shot.duration <= MAX_GENERATION_SECONDS &&
    orders.length > 1 &&
    (shot.order === Math.min(...orders) || shot.order === Math.max(...orders))
  );
}
/**
 * The room views a Pipeline 2 episode needs drawn: one per place and camera view its sequences
 * cut to, named `location — view`, the key a drawn view is stored and reused under.
 */
export function sequenceViews(shots: ShotSpec[]) {
  return [
    ...new Set(shots.flatMap((s) => (s.cuts ?? []).map((c) => setViewName(s.location, c.view)))),
  ];
}
export const setViewName = (location: string, view: string) => `${location.trim()} — ${view}`;
export function usableVideo(
  version: ProjectDetail['versions'][number] | undefined,
  assets: ProjectDetail['assets'],
  bibleVersion: number,
  now = Date.now(),
) {
  const asset = assets.find((a) => a._id === version?.videoAssetId);
  return !!(
    version?.qaVideo?.passed &&
    version.qaVideo.safe &&
    !version.qaVideo.issues.some((i) => i.severity === 'critical') &&
    (version.bibleVersion === undefined || version.bibleVersion === bibleVersion) &&
    asset &&
    !asset.deleted &&
    asset.retention !== 'deleting' &&
    (!asset.expiresAt || asset.expiresAt > now)
  );
}

// Same conservative plan is shown in the browser and recomputed in the request transaction.
export function episodeAllowance(
  shots: ProjectDetail['shots'],
  versions: ProjectDetail['versions'],
  assets: ProjectDetail['assets'],
  bibleVersion: number,
  model: string = MODELS.video,
  /** Room views already drawn for this season; Pipeline 2 reuses them. Pipeline 1 draws none. */
  plates: string[] = [],
  profile?: ProviderProfile,
) {
  const reserve = (kind: JobKind, fallback: string, duration?: number) =>
    profile
      ? routeReservation(kind, profile.steps[kind]!.model, duration, routeFor(profile, kind))
      : reserveFor(kind, fallback, duration);
  const reusable = shots.filter((s) =>
    usableVideo(
      versions.find((v) => v.shotId === s._id && v.version === s.currentVersion),
      assets,
      bibleVersion,
    ),
  );
  const missing = shots.filter((s) => !reusable.includes(s));
  const videoAllowance = (shot: ShotSpec) => {
    if (!profile)
      return model === MODELS.controlledVideo
        ? reserveFor('VIDEO', model, sourceDuration(shot))
        : takeAllowance(shot, model);
    // The graph reserves each extension against its cumulative output length, not just
    // the final source. Routing another step must not drop those paid requests from the quote.
    // Unsupported model/shot pairs are refused at admission; quoting them must stay renderable.
    return profile.steps.VIDEO!.model === MODELS.video && shot.duration > MAX_GENERATION_SECONDS
      ? takeSegments(shot).reduce((sum, segment) => sum + reserve('VIDEO', model, segment.end), 0)
      : reserve('VIDEO', model, sourceDuration(shot));
  };
  const pipeline2 = profile?.pipeline === 2;
  const perShot = (s: (typeof missing)[number]) =>
    reserve('REFERENCE', MODELS.image) +
    reserve('QA_REFERENCE', MODELS.text) +
    videoAllowance(s.spec) +
    reserve('QA_VIDEO', MODELS.text) +
    // Pipeline 2 watches every take a second time, with sound, for lip sync and acting.
    (pipeline2 ? reserve('QA_PERFORMANCE', MODELS.text) : 0);
  // Room for one shot to go wrong and be made again.
  //
  // The plan used to quote exactly one clean pass per shot, which left the run with nothing to
  // spend on its own recoveries: a retried generation, a reworded shot or a reference remade
  // after a check turned it down all draw on this same cap, and reaching it strands the command
  // rather than ending it. One shot's worth is the honest figure — a single stumble is what
  // actually happens, and quoting for every shot stumbling would price an episode at double what
  // it costs and refuse creators who can afford the real thing.
  const recovery = missing.length
    ? Math.max(...missing.map(perShot)) + reserve('SOFTEN', MODELS.text)
    : 0;
  // Keep the legacy quote field while avoiding empty-room conditioning.
  const newPlates: string[] = [];
  // The score and the review of the finished cut. The score is quoted even when the cut keeps
  // the one it has: quoting a little over is safe, quoting under strands the run.
  // The second take of the opening and closing shots, and the check that compares it.
  const orders = shots.map((s) => s.spec.order);
  const secondTakes = missing.filter((s) => takesTwice(s.spec, orders, model));
  const second = secondTakes.reduce(
    (sum, s) => sum + videoAllowance(s.spec) + reserve('QA_VIDEO', MODELS.text),
    0,
  );
  // Pipeline 2 cuts inside its sequences and computes its trims, so it has no edit-planning step,
  // and it draws each camera view of a place once, before the first sequence filmed there.
  const views = pipeline2
    ? sequenceViews(missing.map((s) => s.spec)).filter((v) => !plates.includes(v))
    : [];
  const finishing =
    reserve('SCORE', MODELS.music) +
    reserve('EPISODE_REVIEW', MODELS.text) +
    (pipeline2 ? 0 : reserve('EDIT_PLAN', MODELS.text)) +
    views.length * (reserve('SET_IMAGE', MODELS.image) + reserve('QA_SET', MODELS.text));
  return {
    reusedShots: reusable.length,
    newShots: missing.length,
    newPlates: newPlates.length + views.length,
    secondTakes: secondTakes.length,
    allowance:
      reserve('CAPTIONS', MODELS.transcription) +
      recovery +
      finishing +
      second +
      missing.reduce((sum, s) => sum + perShot(s), 0),
  };
}
